import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { usePresence } from '@/contexts/PresenceContext'
import { useAuthStore } from '@/app/store/auth'

/** scan-status:update, as backend/src/services/scan-status-notify.js sends it. */
interface ScanStatusUpdate {
    type: 'requested' | 'decided'
    change: {
        id: number
        objectId: string
        objectName: string
        state: string
        requestedBy: { id: number; name: string | null } | null
    }
}

/**
 * App-wide: a scan status change waiting for a second person shows as the pulsing marker on the
 * Scan status changes menu entry - the same marker the ServiceNow CMDB entry has - and it appears
 * the moment the request is made.
 *
 * It was first a pop-up (Andrew, 2026-10-06 morning: "Add a notification when a status change is
 * waiting for approval"). The same afternoon: "Change from a notification to having a pulsing dot
 * on the menu similar to ServiceNow CMDB." So nothing is shown from here any more. What this does is
 * keep the marker honest without waiting for the menu's once-a-minute check: every event from the
 * server refreshes the Scan status changes screen, the menu marker (Sidebar asks for
 * ['scan-status-changes', 'waiting-for-me']) and that object's versions list, so a request appears -
 * and one somebody else has just decided disappears - at once. Who the marker is for is the
 * Sidebar's question (may approve, and not their own request); nothing here needs to know who is
 * signed in.
 *
 * Mounted at the root of the app, so it is on the sign-in page too, where nobody is signed in. The
 * pop-up version asked the server who the user was there; it was refused, the client gave up the
 * session and sent the browser to /login, and the sign-in page reloaded itself a dozen times a
 * second until the threat detector blocked the address. Signed out, this does nothing at all.
 */
export function ScanStatusNotifications() {
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
    return isAuthenticated ? <ScanStatusLiveRefresh /> : null
}

function ScanStatusLiveRefresh() {
    const { socket } = usePresence()
    const queryClient = useQueryClient()

    useEffect(() => {
        if (!socket) return
        const onUpdate = (update: ScanStatusUpdate) => {
            const change = update?.change
            if (!change) return
            queryClient.invalidateQueries({ queryKey: ['scan-status-changes'] })
            queryClient.invalidateQueries({ queryKey: ['objects', change.objectId, 'versions'] })
        }
        socket.on('scan-status:update', onUpdate)
        return () => { socket.off('scan-status:update', onUpdate) }
    }, [socket, queryClient])

    return null
}
